// CreateEventModal.jsx (remplace ton composant actuel par celui-ci)

import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  FaSave,
  FaTimes,
  FaPlus,
  FaTrash,
  FaLayerGroup,
  FaFlag,
  FaMapMarkerAlt,
  FaUsers,
  FaCalendarAlt,
  FaLink,
  FaImage,
} from "react-icons/fa";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../components/Modal/Modal"; // ajuste le path si besoin
import { createEvent } from "../../services/eventServices";

const EMPTY = {
  titre: "",
  description: "",
  start: "",
  end: "",
  type_event: "",
  level_event: "",
  lieu_event: "",
  horaire_event: "",
  photo_url: "",
  agenda_url: "",
  is_active: true,
};

const fmtDateInput = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (isNaN(d.getTime())) return "";
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 10);
};

const formatFR = (d) =>
  d
    ? new Date(d).toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : "—";

const AGE_LABELS = [
  "Eveil",
  "Mini-Poussin(e)",
  "Poussin(e)",
  "Benjamin(e)",
  "Minime",
  "Cadet(te)",
  "Junior",
  "Sénior",
  "Vétéran",
];

export default function CreateEventModal({ isOpen, onClose, refreshList }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const [cateList, setCateList] = useState([]); // UI array
  const [selectedCate, setSelectedCate] = useState("");
  const [imgOk, setImgOk] = useState(true);

  const typeEvents = useMemo(
    () => [
      "Compétition",
      "Animation",
      "Stage & Regroupement Technique",
      "Passage de Grade",
      "Entraînement Départemental",
      "Stage Sportif Départemental",
      "Cours commun Club",
      "Stage Club",
      "Tournoi Interclubs",
      "Autre",
    ],
    []
  );

  const levelEvents = useMemo(
    () => ["Club", "Départemental", "Régional", "National", "International"],
    []
  );

  useEffect(() => {
    if (!isOpen) return;
    // reset à l’ouverture
    setForm({
      ...EMPTY,
      start: fmtDateInput(""),
      end: fmtDateInput(""),
      is_active: true,
    });
    setCateList([]);
    setSelectedCate("");
    setImgOk(true);
    setSaving(false);
  }, [isOpen]);

  const onChange = (e) => {
    const { name, type, checked, value } = e.target;
    setForm((s) => ({ ...s, [name]: type === "checkbox" ? checked : value }));
  };

  const onQuillChange = (value) => {
    setForm((s) => ({ ...s, horaire_event: value }));
  };

  const addCategory = () => {
    if (!selectedCate) return;
    if (cateList.includes(selectedCate)) return;
    setCateList((prev) => [...prev, selectedCate]);
    setSelectedCate("");
  };

  const removeCategory = (c) => setCateList((prev) => prev.filter((x) => x !== c));
  const clearCategories = () => setCateList([]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.titre?.trim() || !form.start) return;

    if (form.end && new Date(form.end) < new Date(form.start)) {
      toast.error("La date de fin ne peut pas être antérieure à la date de début.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        cate_event: cateList.join(","),
        start: form.start || null,
        end: form.end || null,
      };

      await createEvent(payload);
      refreshList?.();
      toast.success("Événement créé avec succès.");
      onClose?.();
    } catch (err) {
      console.error("Erreur lors de la création de l'événement", err);
      toast.error("Erreur lors de la création de l'événement.");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      labelledById="create-event-title"
    >
      <ModalHeader
        id="create-event-title"
        title="Créer un événement"
        subtitle="Renseignez les informations puis validez pour publier dans le calendrier"
        onClose={onClose}
      />

      <ModalBody>
        <div className="grid md:grid-cols-3 gap-0 max-h-[72vh]">
          {/* FORM */}
          <div className="md:col-span-2 pr-1 overflow-y-auto">
            <form onSubmit={submit} className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                {/* Titre */}
                <div className="md:col-span-2">
                  <label className="text-xs uppercase text-slate-500 dark:text-slate-300">
                    Titre *
                  </label>
                  <input
                    name="titre"
                    value={form.titre}
                    onChange={onChange}
                    required
                    className="mt-1 w-full rounded-xl border border-slate-300/80 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 outline-none focus:ring-2 focus:ring-sky-400"
                    placeholder="Ex : Tournoi interclubs"
                  />
                </div>

                {/* Dates */}
                <div>
                  <label className="text-xs uppercase text-slate-500 dark:text-slate-300">
                    Début *
                  </label>
                  <input
                    type="date"
                    name="start"
                    value={form.start}
                    onChange={onChange}
                    required
                    className="mt-1 w-full rounded-xl border border-slate-300/80 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 outline-none focus:ring-2 focus:ring-sky-400"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase text-slate-500 dark:text-slate-300">
                    Fin
                  </label>
                  <input
                    type="date"
                    name="end"
                    value={form.end}
                    onChange={onChange}
                    className="mt-1 w-full rounded-xl border border-slate-300/80 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 outline-none focus:ring-2 focus:ring-sky-400"
                  />
                </div>

                {/* Type / Niveau */}
                <div>
                  <label className="text-xs uppercase text-slate-500 dark:text-slate-300">
                    Type *
                  </label>
                  <select
                    name="type_event"
                    value={form.type_event}
                    onChange={onChange}
                    required
                    className="mt-1 w-full rounded-xl border border-slate-300/80 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 outline-none focus:ring-2 focus:ring-sky-400"
                  >
                    <option value="">Sélectionner</option>
                    {typeEvents.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-xs uppercase text-slate-500 dark:text-slate-300">
                    Niveau *
                  </label>
                  <select
                    name="level_event"
                    value={form.level_event}
                    onChange={onChange}
                    required
                    className="mt-1 w-full rounded-xl border border-slate-300/80 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 outline-none focus:ring-2 focus:ring-sky-400"
                  >
                    <option value="">Sélectionner</option>
                    {levelEvents.map((l) => (
                      <option key={l} value={l}>
                        {l}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Catégories */}
                <div className="md:col-span-2">
                  <div className="flex items-center justify-between">
                    <label className="text-xs uppercase text-slate-500 dark:text-slate-300">
                      Catégories
                    </label>
                    {cateList.length > 0 && (
                      <button
                        type="button"
                        onClick={clearCategories}
                        className="text-xs underline opacity-80 hover:opacity-100"
                      >
                        Vider
                      </button>
                    )}
                  </div>

                  <div className="mt-1 flex items-center gap-2">
                    <select
                      value={selectedCate}
                      onChange={(e) => setSelectedCate(e.target.value)}
                      className="flex-1 rounded-xl border border-slate-300/80 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 outline-none focus:ring-2 focus:ring-sky-400"
                    >
                      <option value="">Sélectionner une catégorie</option>
                      {AGE_LABELS.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>

                    <button
                      type="button"
                      onClick={addCategory}
                      className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-sky-600 text-white hover:bg-sky-700 active:scale-[0.99]"
                      title="Ajouter"
                    >
                      <FaPlus />
                    </button>
                  </div>

                  {cateList.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {cateList.map((cate) => (
                        <span
                          key={cate}
                          className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-xs bg-sky-100 text-sky-800 ring-1 ring-sky-200 dark:bg-sky-900/40 dark:text-sky-200 dark:ring-sky-800/50"
                        >
                          {cate}
                          <button
                            type="button"
                            onClick={() => removeCategory(cate)}
                            className="opacity-80 hover:opacity-100"
                            title={`Retirer ${cate}`}
                          >
                            <FaTrash />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Lieu */}
                <div className="md:col-span-2">
                  <label className="text-xs uppercase text-slate-500 dark:text-slate-300">
                    Lieu
                  </label>
                  <input
                    name="lieu_event"
                    value={form.lieu_event}
                    onChange={onChange}
                    className="mt-1 w-full rounded-xl border border-slate-300/80 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 outline-none focus:ring-2 focus:ring-sky-400"
                    placeholder="Gymnase, Dojo, Ville…"
                  />
                </div>

                {/* Horaires (ReactQuill) */}
                <div className="md:col-span-2">
                  <label className="text-xs uppercase text-slate-500 dark:text-slate-300">
                    Horaires / Informations
                  </label>
                  <div className="mt-1 rounded-xl overflow-hidden border border-slate-300/80 dark:border-slate-700 bg-white dark:bg-slate-900">
                    <ReactQuill
                      value={form.horaire_event}
                      onChange={onQuillChange}
                      className="min-h-[140px]"
                    />
                  </div>
                </div>

                {/* Description */}
                <div className="md:col-span-2">
                  <label className="text-xs uppercase text-slate-500 dark:text-slate-300">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={form.description}
                    onChange={onChange}
                    rows={4}
                    className="mt-1 w-full rounded-xl border border-slate-300/80 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 outline-none focus:ring-2 focus:ring-sky-400"
                    placeholder="Détails, consignes, inscriptions…"
                  />
                </div>

                {/* Photo URL */}
                <div className="md:col-span-2">
                  <label className="text-xs uppercase text-slate-500 dark:text-slate-300">
                    URL Photo
                  </label>
                  <input
                    name="photo_url"
                    value={form.photo_url}
                    onChange={(e) => {
                      setImgOk(true);
                      onChange(e);
                    }}
                    className="mt-1 w-full rounded-xl border border-slate-300/80 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 outline-none focus:ring-2 focus:ring-sky-400"
                    placeholder="https://…"
                  />
                </div>

                {/* Agenda URL */}
                <div className="md:col-span-2">
                  <label className="text-xs uppercase text-slate-500 dark:text-slate-300">
                    URL Agenda / PDF
                  </label>
                  <input
                    name="agenda_url"
                    value={form.agenda_url}
                    onChange={onChange}
                    className="mt-1 w-full rounded-xl border border-slate-300/80 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 outline-none focus:ring-2 focus:ring-sky-400"
                    placeholder="https://…"
                  />
                </div>

                {/* Actif */}
                <div className="md:col-span-2">
                  <label className="inline-flex items-center gap-3 text-sm">
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={form.is_active}
                      onChange={onChange}
                      className="w-4 h-4"
                    />
                    <span className="text-slate-700 dark:text-slate-200">
                      Afficher sur le calendrier public
                    </span>
                  </label>
                </div>
              </div>

              <ModalFooter>
                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex items-center gap-2 px-4 h-11 rounded-xl bg-slate-100 text-slate-800 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
                >
                  <FaTimes /> Annuler
                </button>

                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-4 h-11 rounded-xl bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-60"
                >
                  <FaSave /> {saving ? "Création…" : "Créer l’événement"}
                </button>
              </ModalFooter>
            </form>
          </div>

          {/* PREVIEW */}
          <aside className="md:col-span-1 pl-3 overflow-y-auto border-l border-slate-200 dark:border-slate-800">
            <div className="rounded-2xl overflow-hidden ring-1 ring-slate-200 dark:ring-slate-800 bg-white dark:bg-slate-900">
              <div className="p-3">
                <div className="flex items-center gap-2 mb-2 text-xs uppercase tracking-wider text-slate-500 dark:text-slate-300">
                  <FaImage />
                  Aperçu
                </div>

                <img
                  src={imgOk && form.photo_url ? form.photo_url : "/img/no-image.jpg"}
                  alt={form.titre || "Aperçu"}
                  className="block w-full max-h-[34vh] object-contain rounded-xl bg-slate-50 dark:bg-slate-950"
                  onError={() => setImgOk(false)}
                />
              </div>

              <div className="px-4 pb-4 text-sm text-slate-700 dark:text-slate-200 space-y-2">
                <div className="font-extrabold text-base line-clamp-2">
                  {form.titre || "Titre de l’événement"}
                </div>

                {(form.start || form.end) && (
                  <div className="flex items-center gap-2">
                    <FaCalendarAlt className="opacity-70" />
                    <span>
                      {formatFR(form.start)}
                      {form.end ? ` → ${formatFR(form.end)}` : ""}
                    </span>
                  </div>
                )}

                {form.type_event && (
                  <div className="flex items-center gap-2">
                    <FaLayerGroup className="opacity-70" />
                    <span className="truncate">{form.type_event}</span>
                  </div>
                )}

                {form.level_event && (
                  <div className="flex items-center gap-2">
                    <FaFlag className="opacity-70" />
                    <span className="truncate">{form.level_event}</span>
                  </div>
                )}

                {form.lieu_event && (
                  <div className="flex items-center gap-2">
                    <FaMapMarkerAlt className="opacity-70" />
                    <span className="truncate">{form.lieu_event}</span>
                  </div>
                )}

                {cateList.length > 0 && (
                  <div className="flex items-start gap-2">
                    <FaUsers className="opacity-70 mt-0.5" />
                    <span className="line-clamp-3">{cateList.join(", ")}</span>
                  </div>
                )}

                {form.agenda_url && (
                  <div className="flex items-center gap-2">
                    <FaLink className="opacity-70" />
                    <span className="truncate">{form.agenda_url}</span>
                  </div>
                )}

                <div className="pt-2">
                  <span
                    className={[
                      "inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ring-1",
                      form.is_active
                        ? "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-200 dark:ring-emerald-800/60"
                        : "bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-900/30 dark:text-rose-200 dark:ring-rose-800/60",
                    ].join(" ")}
                  >
                    {form.is_active ? "Actif" : "Inactif"}
                  </span>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </ModalBody>
    </Modal>
  );
}
